import { BuyButton, isStripeUrl } from "./Pay";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import { apiAddReview } from "../../api";
import { startMusic, stopMusic, toggleMusic, subscribeMusic } from "../../lib/bgMusic";
import {
  Star,
  Download,
  UserPlus,
  Play,
  Pause,
  Music,
  X,
  MessageCircle,
} from "lucide-react";

// ---------- helpers ----------
const ctaHref = (variant, value, message) => {
  if (variant === "whatsapp") {
    const phone = (value || "").replace(/[^\d]/g, "");
    const t = message ? `?text=${encodeURIComponent(message)}` : "";
    return `https://wa.me/${phone}${t}`;
  }
  if (variant === "email") return `mailto:${value}`;
  return value || "#";
};

// ========================================================================
// 1) GUARDAR CONTACTO (vCard .vcf)
// ========================================================================
export const VCardBlock = ({ block, profile = null }) => {
  const save = () => {
    const displayName = (block.name && block.name.trim()) || (profile && profile.businessName) || block.org || block.label || "Contacto";
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${displayName};;;;`,
      `FN:${displayName}`,
      block.org ? `ORG:${block.org}` : "",
      block.title ? `TITLE:${block.title}` : "",
      block.phone ? `TEL;TYPE=CELL:${block.phone}` : "",
      block.whatsapp ? `TEL;TYPE=CELL:${(block.whatsapp || "").replace(/[^\d]/g, "")}` : "",
      block.whatsapp ? `URL:https://wa.me/${(block.whatsapp || "").replace(/[^\d]/g, "")}` : "",
      block.email ? `EMAIL:${block.email}` : "",
      block.website ? `URL:${block.website}` : "",
      ...(block.links || []).filter((l) => l && l.url && l.url !== "https://").map((l) => `URL:${l.url}`),
      block.address ? `ADR:;;${block.address};;;;` : "",
      "END:VCARD",
    ].filter(Boolean);
    const blob = new Blob([lines.join("\n")], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${displayName.replace(/\s+/g, "-")}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const accent = block.color || "#2E6BFF";
  return (
    <button
      onClick={save}
      className="flex w-full items-center justify-center gap-2.5 rounded-2xl py-3.5 font-display text-sm font-semibold tracking-wide text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
      style={{ background: accent }}
      data-testid="vcard-save-btn"
    >
      <UserPlus className="h-5 w-5" />
      {block.label || "Guardar contacto"}
    </button>
  );
};

// ========================================================================
// 2) RESEÑAS / TESTIMONIOS
// ========================================================================
export const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className="h-3.5 w-3.5"
        style={{ color: "#FBBF24" }}
        fill={i <= n ? "#FBBF24" : "transparent"}
      />
    ))}
  </div>
);

export const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        data-testid={`review-star-${i}`}
        className="p-0.5"
      >
        <Star
          className="h-6 w-6"
          style={{ color: "#FBBF24" }}
          fill={i <= value ? "#FBBF24" : "transparent"}
        />
      </button>
    ))}
  </div>
);

export const ReviewsBlock = ({ block, live = false, portfolioId }) => {
  const [items, setItems] = useState(block.items || []);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setItems(block.items || []);
  }, [block.items]);

  const allowPublic = live && block.allowPublic !== false && !!portfolioId;

  const submit = async () => {
    if (!name.trim() || !text.trim() || sending) return;
    setSending(true);
    try {
      const review = await apiAddReview(portfolioId, block.id, { name, text, rating });
      setItems((prev) => [review, ...prev]);
      setName("");
      setText("");
      setRating(5);
      setOpen(false);
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (e) {
      /* silencioso */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="reviews-block">
      {block.title && (
        <h3 className="text-center font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60">
          {block.title}
        </h3>
      )}
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pf-hscroll">
        {items.map((r) => (
          <div
            key={r.id}
            className="min-w-[80%] shrink-0 snap-start rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur"
          >
            <p className="flex flex-wrap items-center gap-1.5 font-display text-sm font-semibold text-white">
              {r.name}
              {r.purchase && (
                <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-semibold text-blue-200">
                  Compra verificada
                </span>
              )}
            </p>
            <div className="mt-1">
              <Stars n={r.rating || 5} />
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-white/75">"{r.text}"</p>
          </div>
        ))}
      </div>
      {allowPublic &&
        (!open ? (
          <button
            onClick={() => setOpen(true)}
            data-testid="reviews-write-btn"
            className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20"
          >
            {done ? "¡Gracias por tu reseña!" : "Escribir una reseña"}
          </button>
        ) : (
          <div className="space-y-2.5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              data-testid="review-name-input"
              className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none placeholder:text-white/40"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/60">Tu calificación</span>
              <StarPicker value={rating} onChange={setRating} />
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Escribe tu comentario..."
              data-testid="review-text-input"
              className="w-full resize-none rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none placeholder:text-white/40"
            />
            <div className="flex gap-2">
              <button
                onClick={submit}
                disabled={sending}
                data-testid="review-submit-btn"
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-500 disabled:opacity-60"
              >
                {sending ? "Enviando..." : "Publicar reseña"}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70"
              >
                Cancelar
              </button>
            </div>
          </div>
        ))}
    </div>
  );
};

// ========================================================================
// 3) MENÚ / LISTA DE PRECIOS
// ========================================================================
export const MenuBlock = ({ block }) => {
  const currency = block.currency || "$";
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur">
      {block.title && (
        <div className="border-b border-white/10 px-4 py-3">
          <h3 className="font-display text-sm font-bold uppercase tracking-wide text-white">
            {block.title}
          </h3>
        </div>
      )}
      <div className="divide-y divide-white/5">
        {(block.items || []).map((it) => (
          <div key={it.id} className="flex items-center gap-3 px-4 py-3">
            {it.image && (
              <img
                src={it.image}
                alt={it.name}
                className="h-12 w-12 shrink-0 rounded-lg object-cover"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-semibold text-white">{it.name}</p>
              {it.description && (
                <p className="truncate text-xs text-white/55">{it.description}</p>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className="font-display text-sm font-bold text-blue-300">
                {currency}
                {it.price}
              </span>
              {isStripeUrl(it.payLink) && <BuyButton href={it.payLink} small label="Comprar" />}
            </div>
          </div>
        ))}
      </div>
      {block.showOrder && (
        <a
          href={ctaHref("whatsapp", block.orderPhone, block.orderMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#25D366] py-3 font-display text-sm font-semibold text-white transition hover:brightness-110"
        >
          <MessageCircle className="h-4 w-4" />
          {block.orderLabel || "Pedir por WhatsApp"}
        </a>
      )}
    </div>
  );
};

// ========================================================================
// 4) OFERTAS CON CUENTA REGRESIVA
// ========================================================================
const pad = (n) => String(n).padStart(2, "0");
export const CountdownBlock = ({ block }) => {
  const target = block.targetDate ? new Date(block.targetDate).getTime() : 0;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const expired = target > 0 && diff === 0;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const accent = block.color || "#EF4444";
  const cells = [
    { v: d, l: "Días" },
    { v: h, l: "Hrs" },
    { v: m, l: "Min" },
    { v: s, l: "Seg" },
  ];
  return (
    <div
      className="overflow-hidden rounded-2xl border p-5 text-center backdrop-blur"
      style={{ borderColor: `${accent}66`, background: `${accent}1a` }}
    >
      {block.title && (
        <h3 className="font-display text-base font-bold uppercase tracking-wide text-white">
          {block.title}
        </h3>
      )}
      {block.subtitle && (
        <p className="mt-1 text-xs text-white/70">{block.subtitle}</p>
      )}
      {expired ? (
        <p className="mt-4 font-display text-lg font-bold" style={{ color: accent }}>
          ¡La oferta ha terminado!
        </p>
      ) : (
        <div className="mt-4 flex justify-center gap-2.5">
          {cells.map((c) => (
            <div
              key={c.l}
              className="min-w-[58px] rounded-xl bg-black/30 px-2 py-2.5"
              style={{ boxShadow: `inset 0 0 0 1px ${accent}44` }}
            >
              <div
                className="font-display text-2xl font-extrabold tabular-nums"
                style={{ color: accent }}
              >
                {pad(c.v)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/55">
                {c.l}
              </div>
            </div>
          ))}
        </div>
      )}
      {block.btnLabel && !expired && (
        <a
          href={ctaHref(block.btnVariant, block.btnValue, block.btnMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 font-display text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          style={{ background: accent }}
        >
          {block.btnLabel}
        </a>
      )}
    </div>
  );
};

// ========================================================================
// 5) LÍNEA DE TIEMPO / TRAYECTORIA
// ========================================================================
export const TimelineBlock = ({ block }) => {
  const accent = block.color || "#2E6BFF";
  return (
    <div className="space-y-1">
      {block.title && (
        <h3 className="mb-3 text-center font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60">
          {block.title}
        </h3>
      )}
      <div className="relative pl-8">
        <span
          className="absolute bottom-2 left-[11px] top-2 w-0.5"
          style={{ background: `${accent}55` }}
        />
        {(block.items || []).map((it) => (
          <div key={it.id} className="relative pb-6 last:pb-0">
            <span
              className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-black/20"
              style={{ background: accent }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
            </span>
            {it.year && (
              <span
                className="font-display text-xs font-bold"
                style={{ color: accent }}
              >
                {it.year}
              </span>
            )}
            <p className="font-display text-sm font-semibold text-white">{it.title}</p>
            {it.text && (
              <p className="mt-0.5 text-xs leading-relaxed text-white/65">{it.text}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ========================================================================
// 6) REPRODUCTOR DE AUDIO (+ música de fondo desde audio o video)
// ========================================================================
const BgMusicFloat = ({ block }) => {
  const [playing, setPlaying] = useState(false);
  useEffect(() => subscribeMusic(setPlaying), []);
  useEffect(() => {
    startMusic(block.url);
    return stopMusic;
  }, [block.url]);
  if (!block.url) return null;
  return <MusicFloatBtn playing={playing} onClick={toggleMusic} accent={block.color || "#7C3AED"} />;
};

const MusicFloatBtn = ({ playing, onClick, accent }) =>
  createPortal(
    <button
      onClick={onClick}
      className={`fixed right-3 z-[60] flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/45 shadow-[0_6px_20px_rgba(0,0,0,0.35)] backdrop-blur-md transition active:scale-90 ${
        playing ? "audio-float-on" : ""
      }`}
      style={{ top: "max(18vh, 96px)", color: accent === "#7C3AED" || accent === "#2E6BFF" ? "#fff" : accent }}
      data-testid="audio-bg-float"
      aria-label={playing ? "Pausar música" : "Reproducir música"}
    >
      {playing ? (
        <span className="eq eq-playing eq-sm">
          <span className="eq-bar" />
          <span className="eq-bar" />
          <span className="eq-bar" />
          <span className="eq-bar" />
        </span>
      ) : (
        <Music className="h-5 w-5" />
      )}
    </button>,
    document.body
  );

export const AudioBlock = (props) =>
  !props.editable && props.block.mode !== "card" ? <BgMusicFloat block={props.block} /> : <LocalAudioBlock {...props} />;

const LocalAudioBlock = ({ block, editable = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const isBg = block.mode !== "card";

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  }, []);

  // Intenta reproducir la música de fondo apenas carga la página. Si el
  // navegador bloquea el autoplay con sonido, arranca al primer toque.
  useEffect(() => {
    if (!isBg) return;
    const el = audioRef.current;
    if (!el) return;
    const EVTS = ["pointerdown", "touchend", "click", "keydown"];
    const off = () => EVTS.forEach((ev) => window.removeEventListener(ev, start, true));
    // Si el navegador bloquea el autoplay con sonido, arranca en la primera interacción.
    function start(e) {
      if (e && e.target?.closest?.("[data-testid=audio-bg-float]")) return off();
      el.play().then(off).catch(() => {});
    }
    el.play()
      .then(off)
      .catch(() => EVTS.forEach((ev) => window.addEventListener(ev, start, true)));
    return off;
  }, [isBg, block.url]);

  const accent = block.color || "#7C3AED";
  // Música de fondo: en PÚBLICO solo se ve la píldora del borde derecho.
  // La tarjeta se muestra ÚNICAMENTE en el editor (para poder editarla).
  const showCard = isBg ? editable : editable || !block.hideCard;

  return (
    <>
      <audio
        ref={audioRef}
        src={block.url}
        loop
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        preload="auto"
      />

      {showCard && (
        <div
          className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur"
          data-testid="audio-block"
        >
          <button
            onClick={toggle}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-lg transition hover:brightness-110"
            style={{ background: accent }}
            data-testid="audio-toggle-btn"
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-0.5" />}
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-semibold text-white">
              {block.title || "Audio"}
            </p>
            {block.subtitle && (
              <p className="truncate text-xs text-white/55">{block.subtitle}</p>
            )}
            {isBg && (
              <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-white/45">
                <Music className="h-3 w-3" /> Icono flotante · inicia sola · en bucle
              </span>
            )}
          </div>
        </div>
      )}

      {/* Icono flotante pequeño de música (derecha, parte superior) */}
      {isBg && block.url && <MusicFloatBtn playing={playing} onClick={toggle} accent={accent} />}
    </>
  );
};

// ========================================================================
// 7) HISTORIAS TIPO INSTAGRAM
// ========================================================================
export { StoriesBlock } from "./Stories";

// ========================================================================
// 9) IMAGEN (insertar una sola imagen, con tamaño y recorte)
// ========================================================================
const RATIO_MAP = {
  auto: undefined,
  square: "1 / 1",
  "4:3": "4 / 3",
  "16:9": "16 / 9",
  "3:4": "3 / 4",
};
export const ImageBlock = ({ block }) => {
  const width = typeof block.width === "number" ? block.width : 100;
  const ratio = block.ratio || "auto";
  const align = block.align || "center";
  const radius = typeof block.radius === "number" ? block.radius : 16;
  const posX = typeof block.posX === "number" ? block.posX : 50;
  const posY = typeof block.posY === "number" ? block.posY : 50;
  const zoom = typeof block.zoom === "number" ? block.zoom : 1;
  const alignClass =
    align === "left" ? "mr-auto" : align === "right" ? "ml-auto" : "mx-auto";

  if (!block.url) {
    return (
      <div className="mx-auto flex h-40 w-full items-center justify-center rounded-2xl border border-dashed border-white/20 text-sm text-white/40">
        Sube una imagen
      </div>
    );
  }

  if (ratio === "auto") {
    return (
      <div className={`block ${alignClass}`} style={{ width: `${width}%` }}>
        <img
          src={block.url}
          alt=""
          className="w-full"
          style={{ borderRadius: radius, display: "block" }}
        />
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden ${alignClass}`}
      style={{ width: `${width}%`, aspectRatio: RATIO_MAP[ratio], borderRadius: radius }}
    >
      <img
        src={block.url}
        alt=""
        className="h-full w-full object-cover"
        style={{
          objectPosition: `${posX}% ${posY}%`,
          transform: `scale(${zoom})`,
          transformOrigin: `${posX}% ${posY}%`,
        }}
      />
    </div>
  );
};

// ========================================================================
// 10) CÓDIGO QR
// ========================================================================
export const QrBlock = ({ block }) => {
  const [src, setSrc] = useState("");
  const value =
    block.url ||
    (typeof window !== "undefined" ? window.location.href : "https://");
  const fg = block.fg || "#0A1633";
  const bg = block.bg || "#ffffff";

  useEffect(() => {
    QRCode.toDataURL(value, {
      width: 640,
      margin: 2,
      color: { dark: fg, light: bg },
    })
      .then(setSrc)
      .catch(() => setSrc(""));
  }, [value, fg, bg]);

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = "codigo-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
      {block.title && (
        <h3 className="font-display text-sm font-bold uppercase tracking-wide text-white">
          {block.title}
        </h3>
      )}
      {src && (
        <img
          src={src}
          alt="Código QR"
          className="h-44 w-44 rounded-xl bg-white p-2"
          data-testid="qr-image"
        />
      )}
      {block.caption && (
        <p className="text-center text-xs text-white/60">{block.caption}</p>
      )}
      <button
        onClick={download}
        className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 font-display text-sm font-semibold text-white transition hover:brightness-110"
        data-testid="qr-download-btn"
      >
        <Download className="h-4 w-4" /> Descargar QR
      </button>
    </div>
  );
};
